import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { Breadcrumb, Icon, Input, Label } from "../../ui";
import { DeleteConfirmationModal } from "../../ui/Modals";
import { useMetaAdsAdSetForEdit } from "../../../hooks/metaAds/useMetaAdsAdSetForEdit";
import { useMetaAdsDeleteAdSet } from "../../../hooks/metaAds/useMetaAdsDeleteAdSet";
import {
  isMetaAdsApiConfigured,
  patchMetaAdsAdSet,
} from "../../../hooks/metaAds/useMetaAdsApi";
import { isMetaAdsExperienceAvailable } from "../../../hooks/metaAds/useMetaAdsMode";
import { cn } from "@/lib/utils";
import {
  metaAdsDangerZoneDeleteButtonClass,
  metaAdsFormFieldLabelClass,
  metaAdsFormSectionClass,
  metaAdsGradientCtaClass,
  metaAdsOutlineCtaClass,
  metaAdsTitleGradientClass,
} from "./metaAdsTheme";
import {
  formatMetaAdsAdSetOptimizationGoal,
  formatMetaAdsMoneyFromCents,
} from "./metaAdsUtils";

const MetaAdsEditAdSetPage = () => {
  const { adSetId } = useParams();
  const { t } = useTranslation("metaAds");
  const { t: tNav } = useTranslation("navigation");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const apiConfigured = isMetaAdsApiConfigured();
  const unlocked = isMetaAdsExperienceAvailable(apiConfigured);
  const normalizedAdSetId = String(adSetId ?? "").trim();

  const {
    data: adSetDetail,
    isLoading,
    isError,
    error,
  } = useMetaAdsAdSetForEdit(normalizedAdSetId, {
    enabled: Boolean(unlocked && normalizedAdSetId),
  });

  const [name, setName] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const hydratedKeyRef = useRef(null);

  useEffect(() => {
    if (!adSetDetail?.adSetId) {
      return;
    }
    const key = String(adSetDetail.adSetId);
    if (hydratedKeyRef.current === key) {
      return;
    }
    hydratedKeyRef.current = key;
    setName(adSetDetail.name || "");
  }, [adSetDetail]);

  const hasAdSetChanges = Boolean(
    adSetDetail && name.trim() !== String(adSetDetail.name ?? "").trim(),
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: tNav("sidebar.growth"), disabled: true },
      {
        label: tNav("sidebar.metaAds"),
        path: "/growth/meta-ads",
        icon: "Share2",
      },
      {
        label: t("create.editAdSetBreadcrumbLabel"),
        active: true,
        disabled: true,
      },
    ],
    [t, tNav],
  );

  const saveMutation = useMutation({
    mutationFn: (body) => patchMetaAdsAdSet(normalizedAdSetId, body),
    onSuccess: (data) => {
      showToast(t("create.editAdSetSuccessToast"), "success");
      queryClient.setQueryData(
        ["meta-ads-adset-detail", normalizedAdSetId],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: ["meta-ads-graph-campaigns"],
        exact: false,
      });
    },
    onError: (err) => {
      showToast(err?.message || t("create.editAdSetSaveErrorToast"), "error");
    },
  });
  const deleteAdSetMutation = useMetaAdsDeleteAdSet();

  const handleConfirmDeleteAdSet = () => {
    if (!normalizedAdSetId) {
      setDeleteModalOpen(false);
      return;
    }
    deleteAdSetMutation.mutate(
      { adSetId: normalizedAdSetId },
      {
        onSuccess: () => {
          setDeleteModalOpen(false);
          navigate("/growth/meta-ads/performance");
        },
      },
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!normalizedAdSetId || !name.trim()) {
      showToast(t("create.adSetNameRequired"), "error");
      return;
    }
    saveMutation.mutate({ name: name.trim() });
  };

  const disableSubmit =
    !unlocked ||
    isLoading ||
    isError ||
    saveMutation.isPending ||
    !name.trim() ||
    !hasAdSetChanges;

  return (
    <div className="space-y-10">
      <Breadcrumb items={breadcrumbItems} />

      {!unlocked && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          {t("hub.apiNotConfigured")}
        </div>
      )}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            <span className={metaAdsTitleGradientClass}>
              {t("create.editAdSetTitleGradientWord")}
            </span>
            <span className="text-foreground">
              {t("create.editAdSetTitleRest")}
            </span>
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">
            {t("create.editAdSetSubtitle")}
          </p>
        </div>
        <Link
          to="/growth/meta-ads/performance"
          className={cn(
            metaAdsOutlineCtaClass,
            "focus-visible:ring-brand-cyan/35 inline-flex w-full shrink-0 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none sm:w-auto",
          )}
        >
          {t("create.cancelToPerformance")}
        </Link>
      </div>

      {isLoading ? (
        <section className={metaAdsFormSectionClass}>
          <div className="bg-muted/30 h-28 animate-pulse rounded-xl" />
        </section>
      ) : isError ? (
        <div className="border-destructive/35 bg-destructive/10 rounded-xl border px-4 py-6 text-sm text-red-100">
          {error?.message || t("create.editAdSetError")}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <section className={metaAdsFormSectionClass}>
            <div className="mb-5 flex items-center gap-2">
              <Icon
                name="CheckCircle2"
                className="h-5 w-5 text-emerald-400"
                aria-hidden
              />
              <h2 className="text-foreground text-lg font-semibold">
                {t("create.adSetDetailsSection")}
              </h2>
            </div>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label
                  htmlFor="meta-adset-name"
                  className={metaAdsFormFieldLabelClass}
                >
                  {t("create.adSetName")}
                </Label>
                <Input
                  id="meta-adset-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t("create.adSetNamePlaceholder")}
                  required
                  variant="dark"
                  size="lg"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="border-border/60 bg-muted/10 rounded-xl border p-4">
                  <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    {t("performance.colCampaign")}
                  </p>
                  <p className="text-foreground mt-2 text-sm font-medium">
                    {adSetDetail?.campaignName ||
                      adSetDetail?.campaignId ||
                      t("performance.comingSoon")}
                  </p>
                </div>
                <div className="border-border/60 bg-muted/10 rounded-xl border p-4">
                  <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    {t("performance.colOptimizationGoal")}
                  </p>
                  <p className="text-foreground mt-2 text-sm font-medium">
                    {formatMetaAdsAdSetOptimizationGoal(
                      adSetDetail?.optimizationGoal,
                      t,
                    )}
                  </p>
                </div>
                <div className="border-border/60 bg-muted/10 rounded-xl border p-4">
                  <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    {t("performance.colDailyBudget")}
                  </p>
                  <p className="text-foreground mt-2 text-sm font-medium">
                    {formatMetaAdsMoneyFromCents(adSetDetail?.dailyBudgetCents)}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <button
            type="submit"
            disabled={disableSubmit}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-base font-semibold transition disabled:cursor-not-allowed disabled:opacity-50",
              metaAdsGradientCtaClass,
            )}
          >
            {saveMutation.isPending ? (
              t("create.editSubmitting")
            ) : (
              <>
                <Icon name="Save" className="h-5 w-5" aria-hidden />
                {t("create.editSubmitLabel")}
              </>
            )}
          </button>

          <DeleteConfirmationModal
            confirmLoadingText={t("create.deleteAdSetDeleting")}
            detailNote={t("create.deleteAdSetIrreversible")}
            isLoading={deleteAdSetMutation.isPending}
            isOpen={deleteModalOpen}
            itemName={name.trim() || normalizedAdSetId}
            itemType={t("create.deleteAdSetItemType")}
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={handleConfirmDeleteAdSet}
            subtitle={t("create.deleteAdSetModalSubtitle")}
            title={t("create.deleteAdSetModalTitle")}
            confirmButtonText={t("create.deleteAdSetConfirm")}
          />
          <div className="border-border/70 mt-14 border-t pt-10">
            <h2 className="text-foreground mb-5 text-xl font-semibold tracking-tight">
              {t("create.deleteCampaignSectionLabel")}
            </h2>
            <div className="bg-muted/25 rounded-xl border border-red-500/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
              <div className="flex flex-col gap-6 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-6 sm:py-6">
                <div className="min-w-0 space-y-2 sm:max-w-2xl sm:flex-1">
                  <h3 className="text-foreground text-base leading-snug font-semibold sm:text-[17px]">
                    {t("create.deleteAdSetDangerHeading")}
                  </h3>
                  <p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
                    {t("create.deleteAdSetSectionHint")}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={
                    !unlocked ||
                    isLoading ||
                    Boolean(isError) ||
                    deleteAdSetMutation.isPending ||
                    saveMutation.isPending
                  }
                  onClick={() => setDeleteModalOpen(true)}
                  className={cn(
                    metaAdsDangerZoneDeleteButtonClass,
                    "sm:w-auto",
                  )}
                >
                  {deleteAdSetMutation.isPending ? (
                    <Icon
                      name="Loader2"
                      className="h-4 w-4 animate-spin"
                      aria-hidden
                    />
                  ) : (
                    <Icon name="Trash2" className="h-4 w-4" aria-hidden />
                  )}
                  {deleteAdSetMutation.isPending
                    ? t("create.deleteAdSetDeleting")
                    : t("create.deleteAdSet")}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default MetaAdsEditAdSetPage;
